/**
 * Random password generation for the "Require password" share-dialog field
 * (Task 9) and EditShareGrantDialog's add/regenerate password actions. Pure
 * helper, no API calls.
 *
 * Used both to pre-fill the password field the moment password protection
 * is turned on (so an admin sees a ready-to-use value instead of an empty
 * field — they can always edit it or click "Generate" for a different one)
 * and by the "Generate"/"Regenerate password" buttons themselves.
 *
 * Previously this pre-fill was derived from `{brandName}_{campaign
 * prefix}_{campCode}` — deterministic from data visible on the page. That
 * was a real weakness: `campCode` is always present in the URL being
 * shared, so a third of the "password" was public the moment the link
 * existed, for every campaign whose brand/campaign name a guesser could
 * see. A random value has no such correlation to public data.
 */

const DEFAULT_RANDOM_LEN = 12;
const ALNUM_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';

/**
 * Fresh random password — lowercase-alnum alphabet. Uses
 * `crypto.getRandomValues` when available (browsers, jsdom) for unbiased
 * sampling; falls back to `Math.random` only if `crypto` is unavailable (no
 * new dependency needed for either path).
 */
export function generateRandomSharePassword(length: number = DEFAULT_RANDOM_LEN): string {
  const values = new Uint32Array(length);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(values);
  } else {
    for (let i = 0; i < length; i += 1) {
      values[i] = Math.floor(Math.random() * 0xffffffff);
    }
  }
  return Array.from(values, (v) => ALNUM_ALPHABET[v % ALNUM_ALPHABET.length]).join('');
}
